import React, { useMemo } from 'react';

import type { ColumnDef } from '@tanstack/react-table';
import { DuplicateIcon } from '@vlabs/icons';
import { DeleteListsIconButton } from '@vlabs/monokit/features/delete-lists';
import { GoToListButton } from '@vlabs/monokit/features/go-to-list';
import { ShowCardsCountButton } from '@vlabs/monokit/features/show-cards-count';
import type { List } from '@vlabs/api/luna-api/model';
import { TableViewSelectable } from '@vlabs/monokit/shared/ui/table-view';
import { dateFormatter } from '@vlabs/monokit/shared/ui/table-view/cell/formatters/date';
import { Control } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';

import {
  titleCell, renderCell, renderHeader, createTimeCell, lastUpdateTimeCell,
  showCardsCell, actionCell, dividerCell,
} from './cells';
import { ListListTableHead } from './ListListTableHead';

interface ListsTableProps {
  data: List[];
  onSelect: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  selectedIds: Record<string, boolean>;
}

export function ListListTable({
  data,
  onSelect,
  selectedIds,
}: ListsTableProps): React.JSX.Element {
  const { t } = useTranslation();

  const columns: ColumnDef<List>[] = useMemo(() => ([
    {
      accessorKey: 'user_data',
      header: renderHeader<string>(titleCell, t('list-list-page:table.titles.name')),
      cell: renderCell(titleCell),
    },
    {
      accessorKey: 'create_time',
      header: renderHeader<string>(createTimeCell, t('list-list-page:table.titles.created')),
      cell: renderCell(createTimeCell, (ctx) => dateFormatter(ctx.getValue())),
    },
    {
      accessorKey: 'last_update_time',
      header: renderHeader<string>(lastUpdateTimeCell, t('list-list-page:table.titles.last_update')),
      cell: renderCell(lastUpdateTimeCell, (ctx) => dateFormatter(ctx.getValue())),
    },
    {
      accessorKey: 'list_id',
      header: renderHeader<string>(showCardsCell, t('list-list-page:table.titles.cards_in_list')),
      cell: renderCell(showCardsCell, (ctx) => (<ShowCardsCountButton listId={ctx.getValue()} />)),
    },
    {
      accessorKey: 'list_id',
      id: 'viewListDetail',
      header: renderHeader(actionCell),
      cell: renderCell(actionCell, (ctx) => (<GoToListButton listId={ctx.getValue()} />)),
    },
    {
      accessorKey: 'list_id',
      id: 'copyList',
      header: renderHeader(actionCell),
      // FIXME Декоративная кнопка, заменить на фичу копирования списка
      cell: renderCell(actionCell, () => (
        <Control.IconButton
          disabled
          icon={<DuplicateIcon />}
          kind="secondary"
          shape="round"
          size="medium"
          variant="flat"
        />
      )),
    },
    {
      accessorKey: 'list_id',
      id: 'deleteList',
      header: renderHeader(actionCell),
      cell: renderCell(actionCell, (ctx) => (<DeleteListsIconButton listIds={[ctx.getValue()]} />)),
    },
    { cell: dividerCell(undefined), id: 'divider' },
  ] as ColumnDef<List>[]), [t]);

  return (
    <TableViewSelectable<List>
      columns={columns}
      data={data}
      getRowId={(row) => row.list_id}
      onRowSelectionChange={onSelect}
      rowSelection={selectedIds}
      TableHead={ListListTableHead}
    />
  );
}
